import { sessionService } from 'services/session.service';
import store from 'store';
import { dataActions, requestsActions } from 'reducers';
import { ServiceWorkType, ShutdownHours, Jwt } from './types';
import { GA_appErrorShown, GA_serviceUnavailable } from './gtm-utils';

export const ADDRESS = new Proxy({ value: '' }, {
    get(target, prop, receiver) {
        Reflect.set(target, 'value', store.getState().conf.zamowieniaServiceAddress);
        const prim = Reflect.get(target, 'value');
        const value = prim[prop as any];
        return typeof value === 'function' ? (value as Function).bind(prim) : value;
    }
});

export const API_ADDRESS = new Proxy({ value: '' }, {
    get(target, prop, receiver) {
        Reflect.set(target, 'value', `${ADDRESS}api/`);
        const prim = Reflect.get(target, 'value');
        const value = prim[prop as any];
        return typeof value === 'function' ? (value as Function).bind(prim) : value;
    }
});

export const logout = () => {
    sessionStorage.removeItem('logout');
    sessionService.clearToken();
    store.dispatch({ type: 'USER_LOGOUT' });
};

export const UNIDENTIFIED_ERROR_MESSAGE = 'Wystąpił niezidentyfikowany błąd, spróbuj ponownie później.';

const endpointOf = (url: string): string => {
    try {
        return new URL(url).pathname;
    } catch {
        return url;
    }
};

interface WebServiceResponse {
    data: any;
    measuredTimes: any;
}

interface HttpResponse<T> extends Response {
    parsedBody?: T;
}

const appendJWTAuthHeaders = (headers: Headers) => {
    const token: Jwt | null = sessionService.getToken();
    if (token) {
        const authMessage = `Bearer ${token.token}`;
        headers.append('Authorization', authMessage);
    }
};

const prepareRequest = (args: RequestInit) => {
    if (!args.headers || !(args.headers instanceof Headers)) {
        args.headers = new Headers();
    }
    appendJWTAuthHeaders(args.headers);
};

const http = async <T>(
    request: RequestInfo
): Promise<HttpResponse<T>> => {
    const response: HttpResponse<T> = await fetch(request);
    let disableUnidentifiedError: boolean = false;
    if (response.headers.get('Content-Type') !== 'application/pdf' &&
        response.headers.get('Content-Type') !== 'application/zip' &&
        !(response.headers.get('Content-Type') ?? ' ').includes('image')) {
        try {
            const parsed = await response.json();
            if (parsed.data && parsed.data.error && parsed.wsErr) {
                store.dispatch(requestsActions.setErrorMessage(parsed.data.error));
                GA_appErrorShown({
                    type: 'webservice',
                    message: parsed.data.error,
                    endpoint: endpointOf(response.url),
                    statusCode: response.status
                });
            }
            if (parsed.data && parsed.data.error && parsed.data.error === 'User not authenticated or invalid token') {
                store.dispatch(requestsActions.setInvalidToken(true));
                disableUnidentifiedError = true;
            }
            if (parsed.error && parsed.error === 'Too Many Attempts.') {
                store.dispatch(requestsActions.setTooManyAttempts(true));
                disableUnidentifiedError = true;
            } else {
                store.dispatch(requestsActions.setTooManyAttempts(false));
            }
            response.parsedBody = (parsed as WebServiceResponse).data;
        } catch (ex) {
            throw new Error('Cannot parse response body to json');
        }
    }
    if (response.status === 503) {
        const serviceWorkOrShutdownHours = response.parsedBody as any;
        GA_serviceUnavailable(
            'message' in serviceWorkOrShutdownHours ? 'service_work' : 'shutdown_hours',
            endpointOf(response.url)
        );
        if ('message' in serviceWorkOrShutdownHours) {
            store.dispatch(dataActions.setServiceWorks([serviceWorkOrShutdownHours as ServiceWorkType]));
        } else {
            store.dispatch(dataActions.setShutdownHoursUserInfo(serviceWorkOrShutdownHours as ShutdownHours));
        }
    } else if (!response.ok) {
        const responseMessage: any = response.parsedBody;
        if (response.status === 401 || response.status === 403 || response.status === 404) {
            throw new Error(responseMessage.error);
        } else if (response.status === 429) {
            store.dispatch(requestsActions.setErrorMessage('Inne zapytanie jest w trakcie przetwarzania, spróbuj ponownie za chwilę.'));
            GA_appErrorShown({
                type: 'too_many_attempts',
                message: 'Inne zapytanie jest w trakcie przetwarzania',
                endpoint: endpointOf(response.url),
                statusCode: response.status
            });
        } else {
            if (!disableUnidentifiedError) {
                store.dispatch(requestsActions.setErrorMessage(UNIDENTIFIED_ERROR_MESSAGE));
                GA_appErrorShown({
                    type: 'unidentified',
                    message: responseMessage?.error ?? UNIDENTIFIED_ERROR_MESSAGE,
                    endpoint: endpointOf(response.url),
                    statusCode: response.status
                });
            }
            throw new Error(responseMessage.error);
        }
    }
    return response;
};

export const get = async <T>(
    path: string,
    args: RequestInit = {}
): Promise<HttpResponse<T>> => {
    const preparedRequest: RequestInit = {
        method: 'get',
        ...args
    };
    prepareRequest(preparedRequest);
    return await http<T>(new Request(path, preparedRequest));
};

export const post = async <T>(
    path: string,
    body: any,
    args: RequestInit = {}
): Promise<HttpResponse<T>> => {
    const isFormData = body instanceof FormData;

    const preparedRequest: RequestInit = {
        method: 'post',
        headers: isFormData ? new Headers() : new Headers({ 'Content-Type': 'application/json' }),
        body: isFormData ? body : JSON.stringify(body),
        ...args
    };
    prepareRequest(preparedRequest);
    return await http<T>(new Request(path, preparedRequest));
};

export const put = async <T>(
    path: string,
    body: any,
    args: RequestInit = {}
): Promise<HttpResponse<T>> => {
    const preparedRequest: RequestInit = {
        method: 'put',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        ...args
    };
    prepareRequest(preparedRequest);
    return await http<T>(new Request(path, preparedRequest));
};

export const del = async <T>(
    path: string,
    body: any,
    args: RequestInit = {}
): Promise<HttpResponse<T>> => {
    const preparedRequest: RequestInit = {
        method: 'delete',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        ...args
    };
    prepareRequest(preparedRequest);
    return await http<T>(new Request(path, preparedRequest));
};

export const buildResourceLink = (path: string): string => (
    path.startsWith('http://') || path.startsWith('https://') ? path : `${ADDRESS}${path}`
);
